/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx, css } from '@emotion/react'

import { EuiButton, EuiFlexItem, EuiFlexGroup, useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { StackDeployment } from '@modules/ui-types'
import { withSmallErrorBoundary } from '@modules/cui/Boundary'
import { getKibanaRedirectPageUrl } from '@modules/kibana-redirect/url'
import { AutoOpsButton } from '@modules/auto-ops-components/AutoOpsButton/AutoOpsButton'
import { useConfig } from '@modules/cui/ConfigContext'

import { isDeploymentAvailableInKibana } from '@/lib/stackDeployments/selectors/available'
import { getDeploymentResourceEndpoint } from '@/lib/stackDeployments/selectors/endpoints'

import { useOnboardingTokenFromProfile } from '../../../../modules/discovery-questions-lib/hooks'

import StackDeploymentAdminActionsDropdown from './StackDeploymentAdminActionsDropdown'

import type { FunctionComponent } from 'react'

type Props = {
  deployment: StackDeployment
  hideClusterInsteadOfDelete: boolean
  hideClusterInsteadOfStop: boolean
}

const StackDeploymentAdminActions: FunctionComponent<Props> = ({
  deployment,
  hideClusterInsteadOfDelete,
  hideClusterInsteadOfStop,
}) => {
  const {
    euiTheme: {
      breakpoint: { s: euiBreakpointS },
    },
  } = useEuiTheme()

  return (
    <EuiFlexGroup
      gutterSize='m'
      responsive={false}
      className='fs-unmask'
      css={css({
        justifyContent: 'flex-end',
        [`@media only screen and (max-width: ${euiBreakpointS}px)`]: {
          justifyContent: 'flex-start',
        },
      })}
    >
      <EuiFlexItem grow={false}>
        <AutoOpsButton deployment={deployment} />
      </EuiFlexItem>

      <EuiFlexItem grow={false}>
        <LaunchKibanaButton deployment={deployment} />
      </EuiFlexItem>

      <EuiFlexItem grow={false}>
        <StackDeploymentAdminActionsDropdown
          deployment={deployment}
          hideClusterInsteadOfDelete={hideClusterInsteadOfDelete}
          hideClusterInsteadOfStop={hideClusterInsteadOfStop}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

const LaunchKibanaButton = ({ deployment }: { deployment: StackDeployment }) => {
  const {
    resources: { kibana },
  } = deployment

  const onboardingToken = useOnboardingTokenFromProfile()

  const isDeploymentAvailable = isDeploymentAvailableInKibana(deployment)

  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  if (!kibana || kibana.length === 0) {
    return null
  }

  const href = isAdminConsole
    ? getDeploymentResourceEndpoint({ deployment, sliderInstanceType: `kibana` })
    : getKibanaRedirectPageUrl(`deployment`, deployment.id, onboardingToken)

  return (
    <EuiButton
      href={href}
      isDisabled={!isDeploymentAvailable}
      data-test-id='deployment-launch-kibana'
    >
      <FormattedMessage id='deployment-admin-actions.kibana' defaultMessage='Open Kibana' />
    </EuiButton>
  )
}

export default withSmallErrorBoundary(StackDeploymentAdminActions)
